import { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Heart } from "lucide-react";
import { api } from "../../lib/api";

const GOAL_COLORS = {
  torahya: "#B8893B",
  yeshivot: "#1E2A3A",
  charity: "#F2E6CB",
};
const GOAL_LABELS = {
  torahya: "תוריא",
  yeshivot: "ישיבות",
  charity: "צדקה",
};

export default function DonationsChart() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .adminChartDonations({ include_dummy: false })
      .then((r) => !cancelled && setStats(r))
      .catch(() => !cancelled && setStats({ total_donations: 0, by_goal: {} }))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const pieData = stats
    ? Object.entries(stats.by_goal || {})
        .map(([key, v]) => ({
          key,
          name: GOAL_LABELS[key] || key,
          value: v.count,
          amount_ils: v.amount_ils,
          amount_usd: v.amount_usd,
          percentage: v.percentage,
        }))
        .filter((d) => d.value > 0)
    : [];

  const empty = !loading && (!stats || stats.total_donations === 0);

  return (
    <div
      className="bg-card/40 rounded-md border border-gold/25 p-5"
      data-testid="donations-chart"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-sm tracking-[0.2em] uppercase text-gold flex items-center gap-2">
          <Heart className="h-4 w-4" strokeWidth={1.5} />
          תרומות לפי מטרה
        </h3>
        {stats && stats.total_donations > 0 && (
          <p className="text-xs text-muted-foreground">
            סה״כ: {stats.total_ils > 0 && `₪${stats.total_ils}`}
            {stats.total_usd > 0 && (stats.total_ils > 0 ? " · " : "") + `$${stats.total_usd}`}
            <span className="mr-1">({stats.total_donations} תרומות)</span>
          </p>
        )}
      </div>

      <div className="h-[260px] flex" data-testid="donations-chart-canvas">
        {loading ? (
          <div className="m-auto text-xs text-muted-foreground">טוען…</div>
        ) : empty ? (
          <div className="m-auto text-center">
            <p className="text-sm text-muted-foreground">עוד אין תרומות.</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              שתף את דף התרומה כדי להתחיל.
            </p>
          </div>
        ) : (
          <>
            <div className="flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    innerRadius={42}
                    paddingAngle={2}
                    stroke="rgba(184,137,59,0.4)"
                  >
                    {pieData.map((d) => (
                      <Cell key={d.key} fill={GOAL_COLORS[d.key]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "var(--card)",
                      border: "1px solid rgba(184,137,59,0.4)",
                      borderRadius: 4,
                      fontSize: 12,
                      direction: "rtl",
                    }}
                    formatter={(value, _name, item) => {
                      const d = item.payload;
                      const lines = [];
                      if (d.amount_ils) lines.push(`₪${d.amount_ils}`);
                      if (d.amount_usd) lines.push(`$${d.amount_usd}`);
                      return [
                        `${value} תרומות · ${lines.join(" · ") || "—"}`,
                        d.name,
                      ];
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Custom legend — recharts default legend doesn't show
                counts/amounts. We render our own so the data is
                directly readable next to each colour swatch. */}
            <div className="w-32 pr-3 flex flex-col justify-center text-xs space-y-2">
              {pieData.map((d) => (
                <div key={d.key} className="flex items-center gap-2" data-testid={`pie-legend-${d.key}`}>
                  <span
                    className="inline-block w-3 h-3 rounded-sm"
                    style={{ backgroundColor: GOAL_COLORS[d.key] }}
                  />
                  <div className="leading-tight">
                    <div className="text-foreground">{d.name}</div>
                    <div className="text-muted-foreground">
                      {d.percentage}% · {d.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
